/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getEnvironment } from 'src/core/utils/api/environment';
import { tryParseJson } from 'src/core/utils/base';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export type SerializableAzureSessionStore = Pick<AzureSessionStore, 'subscriptionId' | 'tenantId' | 'translatorStorageAccount'> & {
    csAccountPath: string;
};

export type AzureTokens = { armToken: string; storageToken: string; error?: Error };
export type ServiceKey = {
    value: string;
    displayedUserRole: string;
    keyType: 'aadToken' | 'resourceKey' | 'none';
    userRole: 'owner' | 'reader' | 'writer' | 'none';
};

const storage = getStorage('localStorage');

/**
 * This store represents the user's currently selected
 * Azure information. Based on this current selection,
 * the apps are loaded.
 */
export class AzureSessionStore {
    @observable public tenantId: string;

    @observable public neutralToken: string;

    @observable public subscriptionId: string;

    @observable public serviceKey: ServiceKey;

    @observable public userTokens: AzureTokens;

    @observable public csAccountStore: AzureCsAccountStore;

    @observable public translatorStorageAccount: string;

    constructor() {
        const sessionObject: SerializableAzureSessionStore = tryParseJson(
            storage.getData(LuisConstants.StorageKeys.AzureSessionStorageKey)
        );

        this.neutralToken = null;
        this.tenantId = sessionObject?.tenantId ?? null;
        this.userTokens = { armToken: null, storageToken: null };
        this.subscriptionId = sessionObject?.subscriptionId ?? null;
        this.serviceKey = { keyType: 'none', value: null, userRole: 'none', displayedUserRole: 'none' };
        this.csAccountStore = sessionObject?.csAccountPath ? new AzureCsAccountStore('', { path: sessionObject.csAccountPath }) : null;
        this.translatorStorageAccount = sessionObject?.translatorStorageAccount ?? null;
    }

    /**
     * Returns an object suitable for serialization
     * that contains the session info.
     */
    private get serializable(): SerializableAzureSessionStore {
        return {
            tenantId: this.tenantId,
            subscriptionId: this.subscriptionId,
            csAccountPath: this.csAccountStore?.path,
            translatorStorageAccount: this.translatorStorageAccount
        };
    }

    /**
     * Serializes the current store data to local storage.
     */
    private _serialize(): void {
        storage.setData(LuisConstants.StorageKeys.AzureSessionStorageKey, JSON.stringify(this.serializable));
    }

    /**
     * Sets the neutral token to the given token.
     *
     * @param token The token to set.
     */
    @action
    public setNeutralToken(token: string) {
        this.neutralToken = token;
    }

    /**
     * Sets the current used tenant id.
     *
     * @param id The id of the tenant to set.
     */
    @action
    public setTenantId(id: string) {
        this.tenantId = id;
        this._serialize();
    }

    /**
     * Sets the user tenant information to the given
     * information object.
     *
     * @param info The information to set.
     */
    @action
    public setUserTokens(tokens: Partial<AzureTokens>) {
        this.userTokens = { ...this.userTokens, ...tokens };
    }

    /**
     * Sets the subscription id with the given
     * subscription id.
     *
     * @param id The id to set.
     */
    @action
    public setSubscriptionId(id: string) {
        this.subscriptionId = id;
        this._serialize();
    }

    /**
     * Sets the Cs account data to the session
     *
     * @param csAccount The storable cs account data.
     */
    @action
    public setCsAccountStore(csAccountStore: AzureCsAccountStore) {
        this.csAccountStore = csAccountStore;
        this._serialize();
    }

    /**
     * Sets the service key object to be used for authenticate the service called.
     */
    @action
    public setServiceKey(serviceKeyObj: ServiceKey) {
        if (!getEnvironment().isProduction && serviceKeyObj.keyType === 'aadToken') {
            serviceKeyObj = { ...serviceKeyObj, keyType: 'resourceKey', value: this.csAccountStore.id };
        }

        this.serviceKey = serviceKeyObj;

        if (serviceKeyObj.keyType === 'resourceKey') {
            this.csAccountStore?.setKeyStringsObject({ key1: serviceKeyObj.value, key2: '' });
        }
    }

    /**
     * Sets the translator storage account name.
     */
    @action
    public setTranslatorStorageAccount(translatorStorageAccount: string) {
        this.translatorStorageAccount = translatorStorageAccount;
        this._serialize();
    }
}
